import { BaseLink } from "@app/design-system/components/link";
import { type Lesson } from "@core/grades/types";
import { formatDuration, formatNumber, money, URLS } from "@core/shared/utils";
import { settingsAtom } from "@framework/shared/atoms";
import { platformFeaturesAtom } from "@framework/shared/atoms/platform-features.atom";
import { trans } from "@mongez/localization";
import {
  ArrowUpIcon,
  Clock3Icon,
  EyeIcon,
  PlayIcon,
  StarIcon,
  UserRoundIcon,
} from "lucide-react";
import { LessonLikeButton } from "../LessonLikeButton";

type SubjectLessonCardProps = {
  index: number;
  lesson: Lesson;
};

export function SubjectLessonCard({ index, lesson }: SubjectLessonCardProps) {
  const fallbackImage = settingsAtom.get("general")?.logo?.url;
  const lessonImage = lesson.image?.url || fallbackImage;
  const commentsAndRatingsEnabled =
    platformFeaturesAtom.useValue().commentsAndRatings === true;

  return (
    <article className="hover:shadow-primary-950/10 group relative grid overflow-hidden rounded-[1.75rem] border border-gray-200/80 bg-white shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-800 sm:grid-cols-[12rem_1fr]">
      <BaseLink
        to={URLS.lessons.view(lesson)}
        aria-label={lesson.name}
        className="relative block min-h-48 overflow-hidden bg-gray-100 hover:no-underline dark:bg-gray-800 sm:min-h-full"
      >
        {lessonImage ? (
          <img
            src={lessonImage}
            alt={lesson.name}
            loading="lazy"
            className="absolute inset-0 h-full w-full object-cover transition duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-500 to-violet-600" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950/75 via-transparent to-transparent" />
        <span className="absolute start-3 top-3 flex h-10 w-10 items-center justify-center rounded-2xl border border-white/30 bg-gray-950/55 text-sm font-black text-white backdrop-blur">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="absolute bottom-3 end-3 flex h-10 w-10 items-center justify-center rounded-full bg-white text-primary-700 shadow-lg transition group-hover:scale-110">
          <PlayIcon className="h-4 w-4 fill-current" />
        </span>
      </BaseLink>

      <div className="flex min-w-0 flex-col p-5 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="rounded-full bg-violet-50 px-3 py-1 text-xs font-bold text-violet-700 dark:bg-violet-950/40 dark:text-violet-300">
            {lesson.category?.name || trans("grades.uncategorized")}
          </span>
          <span className="text-sm font-black text-primary-700 dark:text-primary-300">
            {lesson.isFree ? trans("free") : money(lesson.cost)}
          </span>
        </div>

        <BaseLink
          to={URLS.lessons.view(lesson)}
          className="mt-4 hover:no-underline"
        >
          <h3 className="line-clamp-2 text-lg font-black leading-7 text-gray-950 transition group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-300">
            {lesson.name}
          </h3>
        </BaseLink>

        <div className="mt-4 flex items-center gap-2">
          {lesson.instructor?.image?.url ? (
            <img
              src={lesson.instructor.image.url}
              alt={lesson.instructor.name}
              loading="lazy"
              className="h-8 w-8 rounded-full object-cover ring-2 ring-primary-100 dark:ring-primary-900"
            />
          ) : (
            <span className="dark:bg-primary-950/50 flex h-8 w-8 items-center justify-center rounded-full bg-primary-50 text-primary-600 dark:text-primary-300">
              <UserRoundIcon className="h-4 w-4" />
            </span>
          )}
          <span className="truncate text-xs font-semibold text-gray-600 dark:text-gray-300">
            {lesson.instructor?.name || trans("grades.instructor")}
          </span>
        </div>

        <div className="mt-auto flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-gray-100 pt-4 text-xs text-gray-500 dark:border-gray-800 dark:text-gray-400">
          <span className="inline-flex items-center gap-1.5">
            <EyeIcon className="h-3.5 w-3.5" />
            {formatNumber(lesson.views)}
          </span>
          {lesson.duration ? (
            <span className="inline-flex items-center gap-1.5">
              <Clock3Icon className="h-3.5 w-3.5" />
              <span dir="ltr">{formatDuration(lesson.duration)}</span>
            </span>
          ) : null}
          {commentsAndRatingsEnabled && lesson.rating ? (
            <span className="inline-flex items-center gap-1.5">
              <StarIcon className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
              {lesson.rating}
            </span>
          ) : null}
          <LessonLikeButton lesson={lesson} compact />
          <ArrowUpIcon className="ms-auto h-4 w-4 rotate-45 text-primary-500 transition group-hover:-translate-y-0.5" />
        </div>
      </div>
    </article>
  );
}
